import { Chip, Field, Flex, Paper, Span } from "@goodfill/ui-components";
import { PortfolioSummary } from "./PortfolioSummary";
import { PortfolioDeptDistributionComponent } from "./deptDistribution/PortfolioDeptDistributionComponent";
import { PortfolioDistributionComponent } from "./portfolioDistribution/PortfolioDistributionComponent";
import { useRecoilValue } from "recoil";
import { DateTime } from "luxon";
import { pricedBondState, userDataState } from "../../../../state";
import { createAccountHoldings } from "../../AccountHolding";
import { Currency } from "@goodfill/models-typescript";
import { PortfolioHoldingsSection } from "./holdings/PortfolioHoldingsSection";
import { useState } from "react";
import { PortfolioPositions } from "../positions/PortfolioPositions";

type HoldingsOrPositions = "holdings" | "positions";

export const PortfolioSection = () => {
  const pricedBonds = useRecoilValue(pricedBondState);
  const userData = useRecoilValue(userDataState);

  const [holdingsOrPositions, setHoldingsOrPositions] = useState<HoldingsOrPositions>("holdings");

  if (userData === null) {
    return null;
  }

  const moneyBalanceAccount = userData.getBalanceAccount(Currency.USD);

  const balanceAccounts = userData.balanceAccounts;
  const bondBalanceAccounts = balanceAccounts
    // We want only bonds holdings
    .filter((balanceAccount) => balanceAccount.currency === null && balanceAccount.bondId !== null);

  if (moneyBalanceAccount === null) {
    return (
      <Flex
        direction="col"
        verticalMargin={2}
        horizontalMargin={8}
        horizontalPadding={8}
        gap={8}
        width={96}
        justify="center"
      >
        <Paper yellow={true}>
          <Flex horizontalPadding={4} verticalPadding={4}>
            <Span>Your Portfolio has no balance yet.</Span>
          </Flex>
        </Paper>
      </Flex>
    );
  }

  const bondHoldings = createAccountHoldings(pricedBonds, bondBalanceAccounts).filter((holdings) =>
    holdings.bondBalanceAccount.balance.greaterThan(0),
  );

  const currency = moneyBalanceAccount.currency;
  if (currency === null) {
    return <>Error: currency missing in moneyBalanceAccount</>;
  }

  return (
    <Flex direction="col" verticalMargin={2} horizontalMargin={8} horizontalPadding={8} gap={8} width="w-full">
      <PortfolioSummary moneyBalanceAccount={moneyBalanceAccount} bondHoldings={bondHoldings} />
      <PortfolioDeptDistributionComponent bondHoldings={bondHoldings} tradeDate={DateTime.local()} />
      <PortfolioDistributionComponent bondHoldings={bondHoldings} currency={currency} />

      <div className="flex flex-row">
        <Chip
          font="bold"
          size="medium"
          primary={holdingsOrPositions === "holdings"}
          onClick={() => setHoldingsOrPositions("holdings")}
        >
          Holdings
        </Chip>
        <Chip
          font="bold"
          size="medium"
          primary={holdingsOrPositions === "positions"}
          onClick={() => setHoldingsOrPositions("positions")}
        >
          Positions
        </Chip>
      </div>

      {holdingsOrPositions === "holdings" ? (
        <PortfolioHoldingsSection bondHoldings={bondHoldings} currency={currency} />
      ) : holdingsOrPositions === "positions" ? (
        <PortfolioPositions />
      ) : null}
    </Flex>
  );
};
